import { FC, MouseEvent, useState } from 'react';
import { format } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { ru } from 'react-day-picker/locale';

import { useMedia } from '../../../useMedia';
import { DateRangePickerSingleProps } from '../../model/DateRangePickerProps';
import dayPickerClassNames from '../../model/dayPickerClassNames';
import { DATE_FORMAT } from '../../model/formatRange';
import getCalendarWidth from '../../model/getCalendarWidth';
import { parseDateInput } from '../../model/parseDateInput';
import { DESKTOP_QUERY } from '../../model/query';
import useDisabledMatchers from '../../model/useDisabledMatchers';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import NavChevron from '../NavChevron/NavChevron';
import PickerShell from '../PickerShell/PickerShell';
import Trigger, { CommitTextSource } from '../Trigger/Trigger';

import styles from '../DateRangePicker.module.css';

export type SingleDatePickerProps = Omit<DateRangePickerSingleProps, 'mode'>;

/** Выбор одной даты на react-day-picker. */
const SingleDatePicker: FC<SingleDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  numberOfMonths,
  placeholder = 'Выберите дату',
  isDisabled = false,
  isClearable = true,
  className,
}) => {
  const isDesktop = useMedia(DESKTOP_QUERY);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(value ?? new Date());
  const resolvedDisabled = useDisabledMatchers(minDate, maxDate);
  const resolvedNumberOfMonths = numberOfMonths ?? (isDesktop ? 2 : 1);

  const handleSelect = (date: Date | undefined) => {
    onChange?.(date);

    if (date) {
      setOpen(false);
    }
  };

  const handleClear = (event: MouseEvent) => {
    event.stopPropagation();
    onChange?.(undefined);
  };

  const handleCommitText = (text: string, source: CommitTextSource) => {
    if (text.trim() === '') {
      onChange?.(undefined);
      return;
    }

    const parsed = parseDateInput(text);

    if (!parsed) {
      return;
    }

    if (minDate && parsed < minDate) {
      return;
    }

    if (maxDate && parsed > maxDate) {
      return;
    }

    onChange?.(parsed);
    setMonth(parsed);

    if (source === 'enter') {
      setOpen(false);
    }
  };

  const trigger = (
    <Trigger
      displayText={value ? format(value, DATE_FORMAT) : ''}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={isClearable}
      allowTyping={isDesktop}
      className={className}
      onOpen={() => setOpen(true)}
      onIconClick={() => setOpen((previousOpen) => !previousOpen)}
      onClear={handleClear}
      onCommitText={handleCommitText}
    />
  );

  const calendar = (
    <div
      className={styles.calendarContainer}
      style={isDesktop ? { width: getCalendarWidth(resolvedNumberOfMonths) } : undefined}>
      <DayPicker
        mode="single"
        locale={ru}
        selected={value}
        onSelect={handleSelect}
        month={month}
        onMonthChange={setMonth}
        numberOfMonths={resolvedNumberOfMonths}
        disabled={resolvedDisabled}
        captionLayout="dropdown"
        startMonth={minDate}
        endMonth={maxDate}
        navLayout="around"
        className={styles.dayPicker}
        classNames={dayPickerClassNames}
        components={{ Chevron: NavChevron, Dropdown: DropdownSelect }}
      />
    </div>
  );

  return (
    <PickerShell
      trigger={trigger}
      calendar={calendar}
      open={open}
      onOpenChange={setOpen}
    />
  );
};

export default SingleDatePicker;
